import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import DonateCard from "../components/DonateCard";
import { Reveal, SectionHeading } from "../components/Reveal";
import { useLang } from "../i18n";
import { FAQS } from "../data/content";
import { BookOpen, Stethoscope, HandHeart, TreePine, HeartHandshake, ShieldCheck } from "lucide-react";

const WHY = [
  { icon: BookOpen, hi: "शिक्षा तक पहुंच", en: "Access to education" },
  { icon: Stethoscope, hi: "स्वास्थ्य सेवा", en: "Healthcare services" },
  { icon: HandHeart, hi: "महिला आत्मनिर्भरता", en: "Women's self-reliance" },
  { icon: TreePine, hi: "हरियाली और पर्यावरण", en: "Greenery & environment" },
  { icon: HeartHandshake, hi: "जरूरतमंदों की सहायता", en: "Support for the needy" },
  { icon: ShieldCheck, hi: "पारदर्शी उपयोग", en: "Transparent utilization" },
];

const STEPS = [
  { hi: "QR कोड स्कैन करें या बैंक विवरण से ट्रांसफर करें", en: "Scan the QR code or transfer using the bank details" },
  { hi: "अपनी सुविधा अनुसार राशि का चयन करें", en: "Choose an amount as per your convenience" },
  { hi: "दान के बाद रसीद के लिए संस्था से संपर्क करें", en: "Contact the organization for a receipt after donating" },
];

export default function Donate() {
  const { t, pick, lang } = useLang();
  const faqs = FAQS.slice(4, 8);
  return (
    <main>
      <Seo
        title="दान करें — Anansha Welfare Society Donation"
        description="Anansha Welfare Society को दान करें — QR कोड, UPI (173204554817@hdfcbank) या बैंक ट्रांसफर से सुरक्षित सहयोग। आपका दान शिक्षा, स्वास्थ्य और जरूरतमंदों की सहायता में लगता है।"
        path="/donate"
        breadcrumb={["दान करें"]}
      />
      <PageHero
        overline={{ hi: "सहयोग", en: "Contribute" }}
        title={{ hi: t("donate.title"), en: t("donate.title") }}
        description={{ hi: t("donate.sub"), en: t("donate.sub") }}
        testid="donate-page-heading"
      />

      <section className="py-20 bg-ngo-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading
            overline={pick({ hi: "क्यों दान करें", en: "Why Donate" })}
            title={pick({ hi: "आपका सहयोग कहां पहुंचता है", en: "Where Your Support Reaches" })}
            testid="why-donate-heading"
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {WHY.map((w, i) => (
              <Reveal key={i} i={i}>
                <div data-testid={`why-donate-${i}`} className="h-full bg-white rounded-2xl border border-ngo-deep/10 p-5 text-center hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(23,107,58,0.12)] transition-[box-shadow,transform] duration-300">
                  <div className="w-11 h-11 mx-auto rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep">
                    <w.icon size={20} />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-ngo-ink">{pick({ hi: w.hi, en: w.en })}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
            <DonateCard />
            <div className="space-y-6">
              <Reveal>
                <div className="bg-ngo-forest text-ngo-cream rounded-3xl p-8">
                  <h3 className="font-heading font-bold text-xl">{pick({ hi: "दान प्रक्रिया", en: "Donation Process" })}</h3>
                  <ol className="mt-6 space-y-5">
                    {STEPS.map((s, i) => (
                      <li key={i} data-testid={`donate-step-${i}`} className="flex items-start gap-4">
                        <span className="w-8 h-8 shrink-0 rounded-full bg-ngo-gold text-ngo-ink font-heading font-bold flex items-center justify-center text-sm">{i + 1}</span>
                        <p className="text-sm text-ngo-mint/85 leading-relaxed pt-1">{pick(s)}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
              <Reveal i={1}>
                <div className="bg-ngo-mint border border-ngo-deep/10 rounded-3xl p-8">
                  <h3 className="font-heading font-bold text-xl text-ngo-ink">{pick({ hi: "पारदर्शिता संदेश", en: "Transparency Message" })}</h3>
                  <p className="mt-4 text-sm text-ngo-ink/70 leading-relaxed">
                    {pick({
                      hi: "दान का उपयोग संस्था की सामाजिक गतिविधियों, जरूरतमंद लोगों की सहायता, शिक्षा, स्वास्थ्य, महिला सशक्तिकरण और अन्य सामाजिक कार्यक्रमों में किया जाता है। रसीद या पुष्टि के लिए संस्था से संपर्क करें।",
                      en: "Donations are used for the organization's social activities, helping the needy, education, healthcare, women empowerment and other social programs. Contact the organization for receipt or confirmation.",
                    })}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="mt-20 max-w-3xl">
            <SectionHeading
              overline={pick({ hi: "प्रश्न", en: "Questions" })}
              title={pick({ hi: "दान संबंधी प्रश्न", en: "Donation FAQs" })}
              testid="donate-faq-heading"
            />
            <div className="mt-8 space-y-4">
              {faqs.map((f, i) => (
                <Reveal key={i} i={i}>
                  <details data-testid={`donate-faq-${i}`} className="group bg-white rounded-2xl border border-ngo-deep/10 px-6 py-5 open:shadow-[0_14px_36px_rgba(23,107,58,0.10)] transition-shadow duration-300">
                    <summary className="cursor-pointer font-semibold text-ngo-ink list-none flex items-center justify-between gap-4">
                      {lang === "hi" ? f.hi.q : f.en.q}
                      <span className="text-ngo-leaf text-xl group-open:rotate-45 transition-transform duration-300">+</span>
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-ngo-ink/70">{lang === "hi" ? f.hi.a : f.en.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
